import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    VideoCarousel,
    VideoSlide
} from "@/components/confluence/carousel/src";
import { ConfluenceAnchorVariant } from "@/components/signup/bxp-ui-signup/src/components";
import EmbeddedSignupModule, {
    EmbeddedSignupModuleProps
} from "@/components/signup/bxp-ui-signup/src/embedded-signup-module";
import { ClientOnly } from "@/utils/client-only";
import { FeatureExposed } from "@/utils/feature-exposed";

type PaidHeroVideoCarouselProps = {
    embeddedSignup: EmbeddedSignupModuleProps;
    carouselSlides: Array<VideoSlide>;
    pageId: string;
    customSignupHeader?: string;
    shouldUseTransparentBackground?: boolean;
    shouldUseBlockStartPadding?: boolean;
    shouldUseNewBrandBackground?: boolean;
};

const paidHeroContainerStyles = css({
    display: "flex",
    justifyContent: "center"
});

const backgroundGradient = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "linear-gradient(180deg, #C1EDFB 0%, #98E6FF 100%)"
});

const paidHeroContentStyles = css({
    display: "flex",
    minHeight: 750,
    maxWidth: 1272,
    columnGap: 48,
    rowGap: 24,
    width: "100%",
    justifyContent: "center"
});

const blockStartPadding = css({
    paddingBlockStart: 60
});

const signUpFormContainerStyles = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "1 1 100%",
    maxWidth: 528
});

const carouselContainerStyles = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    minWidth: 280,
    flex: "1 1 100%",
    paddingInlineEnd: 32,
    [media.below.sm]: {
        display: "none"
    }
});
const rebrandBackground = css({
    "marginBlockStart": "60px",
    "position": "relative",
    "&:before": {
        content: '""',
        position: "absolute",
        insetBlockStart: "20px",
        insetInlineStart: 0,
        insetInlineEnd: "100px",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#DEEBFF",
        insetBlockEnd: "70px",
        transform: "rotate(-1.25deg)",
        transformOrigin: "center",
        zIndex: -5,
        pointerEvents: "none"
    },
    "&:after": {
        content: '""',
        position: "absolute",
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#DEEBFF",
        insetBlockStart: "63px",
        insetInlineStart: "200px",
        insetInlineEnd: 0,
        insetBlockEnd: 0,
        transform: "rotate(1deg)",
        transformOrigin: "center",
        zIndex: -5,
        pointerEvents: "none"
    }
});

export const PaidHeroVideoCarousel = ({
    pageId,
    embeddedSignup,
    carouselSlides,
    customSignupHeader,
    shouldUseTransparentBackground,
    shouldUseBlockStartPadding = true,
    shouldUseNewBrandBackground
}: PaidHeroVideoCarouselProps) => {
    const intl = useIntl();

    return (
        <FeatureExposed
            experimentKey="cfind-1466-lift-and-shift"
            group="treatmentA"
        >
            <div
                css={[
                    paidHeroContainerStyles,
                    !shouldUseTransparentBackground && backgroundGradient,
                    shouldUseNewBrandBackground && rebrandBackground
                ]}
            >
                <div
                    css={[
                        paidHeroContentStyles,
                        shouldUseBlockStartPadding && blockStartPadding
                    ]}
                >
                    <div css={signUpFormContainerStyles}>
                        <UFOSegment name="conf-lift-and-shift-features-signup-module">
                            <ClientOnly>
                                <EmbeddedSignupModule
                                    {...embeddedSignup}
                                    variantOverride={
                                        pageId as ConfluenceAnchorVariant
                                    }
                                    customSignupHeader={customSignupHeader}
                                />
                            </ClientOnly>
                        </UFOSegment>
                    </div>
                    <div css={carouselContainerStyles}>
                        <UFOSegment name="cfind-1466-carousel">
                            <VideoCarousel
                                aria-label={intl.formatMessage({
                                    id: "ConfluenceSoftwarePage.v4.CarouselHero.AriaLabel",
                                    defaultMessage:
                                        "A display of various confluence features",
                                    description:
                                        "Aria label for the carousel section"
                                })}
                                slides={carouselSlides}
                                id="confluence-features-video-carousel"
                            />
                        </UFOSegment>
                    </div>
                </div>
            </div>
        </FeatureExposed>
    );
};
